import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, generatePath, useParams } from 'react-router';

import { type TabNav, TabsNav } from '@fly/core';
import { FeatureToggle, relativeRoutes, routes } from '@fly/shared';
import { useFeatureToggle } from '@fly/shared-hooks';

export function RepositoryLayout() {
  const { t } = useTranslation();
  const { hasFeature } = useFeatureToggle();

  const { owner, name } = useParams() as { owner: string; name: string };
  const tabs = useMemo(() => {
    const items = [
      {
        dataTestId: 'tab-releases',
        to: generatePath(routes.DASHBOARD.REGISTRY.REPOSITORY.RELEASES, {
          name,
          owner,
        }),
        iconStart: 'release',
        label: t('app.dashboard.releases.releases', {
          defaultValue: 'Releases',
        }),
        active: (pathname) => pathname.endsWith(relativeRoutes.RELEASES),
      },
      {
        dataTestId: 'tab-artifacts',
        to: generatePath(routes.DASHBOARD.REGISTRY.REPOSITORY.ARTIFACTS, {
          name,
          owner,
        }),
        iconStart: 'artifact',
        label: t('app.dashboard.repositories.artifacts', {
          defaultValue: 'Artifacts',
        }),
        active: (pathname) => pathname.endsWith(relativeRoutes.ARTIFACTS),
      },
      {
        dataTestId: 'tab-workflows',
        to: generatePath(routes.DASHBOARD.REGISTRY.REPOSITORY.WORKFLOWS, {
          name,
          owner,
        }),
        iconStart: 'workflow',
        label: t('app.dashboard.workflows.workflows', {
          defaultValue: 'Workflows',
        }),
        active: (pathname) => pathname.endsWith(relativeRoutes.WORKFLOWS),
      },
    ] satisfies TabNav[];

    if (hasFeature(FeatureToggle.DECISION_RECORDS)) {
      items.push({
        dataTestId: 'tab-decision-records',
        to: generatePath(
          routes.DASHBOARD.REGISTRY.REPOSITORY.DECISION_RECORDS,
          {
            name,
            owner,
          }
        ),
        iconStart: 'decision_record',
        label: t('app.dashboard.registry.tabs.decision_records', {
          defaultValue: 'Decision Records',
        }),
        active: (pathname) =>
          pathname.endsWith(relativeRoutes.DECISION_RECORDS),
      } satisfies TabNav);
    }

    return items;
  }, [hasFeature, name, owner, t]);

  return (
    <>
      <TabsNav
        tabs={tabs}
        className='relative -left-fly-7 w-[calc(100%+56px)] pl-fly-5'
      />
      <Outlet />
    </>
  );
}
